import { SystemVehicleType, TransportMode } from './status.enum';

/**
 * Material Icons name mapping for each vehicle type.
 * Used by React Native (MaterialIcons) and web (MUI/Material).
 */
export const VEHICLE_TYPE_MATERIAL_ICON: Record<SystemVehicleType, string> = {
    [SystemVehicleType.AIRPLANE]: 'flight',
    [SystemVehicleType.HELICOPTER]: 'flight',
    [SystemVehicleType.TRUCK]: 'local-shipping',
    [SystemVehicleType.TRAILER]: 'local-shipping',
    [SystemVehicleType.BUS]: 'directions-bus',
    [SystemVehicleType.CAR]: 'directions-car',
    [SystemVehicleType.MOTORCYCLE]: 'two-wheeler',
    [SystemVehicleType.BIKE]: 'pedal-bike',
    [SystemVehicleType.WALKING]: 'directions-walk',
};

/**
 * Emoji icon for each vehicle type — used in seed data and fallback UIs.
 */
export const VEHICLE_TYPE_EMOJI: Record<SystemVehicleType, string> = {
    [SystemVehicleType.AIRPLANE]: '✈️',
    [SystemVehicleType.HELICOPTER]: '🚁',
    [SystemVehicleType.TRUCK]: '🚚',
    [SystemVehicleType.TRAILER]: '🚛',
    [SystemVehicleType.BUS]: '🚌',
    [SystemVehicleType.CAR]: '🚗',
    [SystemVehicleType.MOTORCYCLE]: '🏍️',
    [SystemVehicleType.BIKE]: '🚲',
    [SystemVehicleType.WALKING]: '🚶',
};

/**
 * PT-BR labels for each system vehicle type.
 */
export const VEHICLE_TYPE_LABEL: Record<SystemVehicleType, string> = {
    [SystemVehicleType.AIRPLANE]: 'Avião',
    [SystemVehicleType.HELICOPTER]: 'Helicóptero',
    [SystemVehicleType.TRUCK]: 'Caminhão',
    [SystemVehicleType.TRAILER]: 'Carreta',
    [SystemVehicleType.BUS]: 'Ônibus',
    [SystemVehicleType.CAR]: 'Carro',
    [SystemVehicleType.MOTORCYCLE]: 'Moto',
    [SystemVehicleType.BIKE]: 'Bike',
    [SystemVehicleType.WALKING]: 'A pé',
};

/**
 * Active system vehicle types (excludes legacy large_car/small_car).
 */
export const ACTIVE_SYSTEM_VEHICLE_TYPES: SystemVehicleType[] = [
    SystemVehicleType.AIRPLANE,
    SystemVehicleType.HELICOPTER,
    SystemVehicleType.TRUCK,
    SystemVehicleType.TRAILER,
    SystemVehicleType.BUS,
    SystemVehicleType.CAR,
    SystemVehicleType.MOTORCYCLE,
    SystemVehicleType.BIKE,
    SystemVehicleType.WALKING,
];

/**
 * Helper to get the material icon name for a vehicle type code string.
 * Falls back to 'directions-car' if unknown.
 */
export function getVehicleTypeMaterialIcon(typeCode: string): string {
    return VEHICLE_TYPE_MATERIAL_ICON[typeCode as SystemVehicleType] ?? 'directions-car';
}

/**
 * Helper to get the emoji for a vehicle type code string.
 * Falls back to '🚗' if unknown.
 */
export function getVehicleTypeEmoji(typeCode: string): string {
    return VEHICLE_TYPE_EMOJI[typeCode as SystemVehicleType] ?? '🚗';
}

/**
 * Helper to get the PT-BR label for a vehicle type code string.
 * Falls back to the typeCode itself if unknown.
 */
export function getVehicleTypeLabel(typeCode: string): string {
    return VEHICLE_TYPE_LABEL[typeCode as SystemVehicleType] ?? typeCode;
}

// ── Transport Mode utilities ──────────────────────────────────────────────────

/**
 * PT-BR labels for each transport mode.
 */
export const TRANSPORT_MODE_LABEL: Record<TransportMode, string> = {
    [TransportMode.PERSON]: 'Passageiros',
    [TransportMode.CARGO]: 'Entregas',
};

/**
 * Emoji icon for each transport mode.
 */
export const TRANSPORT_MODE_EMOJI: Record<TransportMode, string> = {
    [TransportMode.PERSON]: '🧍',
    [TransportMode.CARGO]: '📦',
};

/**
 * Helper to get the PT-BR label for a transport mode string.
 * Falls back to the mode string itself if unknown.
 */
export function getTransportModeLabel(mode: string): string {
    return TRANSPORT_MODE_LABEL[mode as TransportMode] ?? mode;
}

/**
 * Helper to get the emoji for a transport mode string.
 * Falls back to '🧍' if unknown.
 */
export function getTransportModeEmoji(mode: string): string {
    return TRANSPORT_MODE_EMOJI[mode as TransportMode] ?? '🧍';
}
